// hooks/useLinkHorizontalScroll.ts
import { useEffect } from 'react';

type ApiRef = React.MutableRefObject<any>;

function getScrollerFromApi(api: ApiRef): HTMLElement | null {
  const viaRef = api?.current?.virtualScrollerRef
    ?.current as HTMLElement | null;
  if (viaRef) return viaRef;
  const root = api?.current?.rootElementRef?.current as HTMLElement | null;
  return root
    ? (root.querySelector('.MuiDataGrid-virtualScroller') as HTMLElement | null)
    : null;
}

export default function useLinkHorizontalScroll(
  apiTop: ApiRef,
  apiBottom: ApiRef,
  deps: any[] = []
) {
  useEffect(() => {
    const top = getScrollerFromApi(apiTop);
    const bottom = getScrollerFromApi(apiBottom);
    if (!top || !bottom) return;

    // Avoid browser smooth-scroll delays on follower
    top.style.scrollBehavior = 'auto';
    bottom.style.scrollBehavior = 'auto';

    const maxLeft = (el: HTMLElement) =>
      Math.max(0, el.scrollWidth - el.clientWidth);

    let programmatic = false;
    let rafId = 0;
    let lastLeftTop = top.scrollLeft;
    let lastLeftBottom = bottom.scrollLeft;
    let lastWheelTop = 0;
    let lastWheelBottom = 0;

    const markWheelTop = () => {
      lastWheelTop = performance.now();
    };
    const markWheelBottom = () => {
      lastWheelBottom = performance.now();
    };
    top.addEventListener('wheel', markWheelTop, { passive: true });
    bottom.addEventListener('wheel', markWheelBottom, { passive: true });

    const writeFollower = (
      src: HTMLElement,
      dst: HTMLElement,
      immediate: boolean
    ) => {
      const srcMax = maxLeft(src);
      const dstMax = maxLeft(dst);
      const pct = srcMax > 0 ? src.scrollLeft / srcMax : 0;
      const target = Math.round(pct * dstMax); // integer px to avoid micro-nudges

      if (programmatic) return;
      const doWrite = () => {
        programmatic = true;
        if (Math.abs(dst.scrollLeft - target) > 1) dst.scrollLeft = target;
        // release on next frame so follower's own 'scroll' doesn't bounce back
        requestAnimationFrame(() => {
          programmatic = false;
        });
      };

      if (immediate) {
        doWrite(); // wheel/trackpad → immediate feels snappier
      } else {
        if (rafId) cancelAnimationFrame(rafId); // thumb drag → coalesce to RAF
        rafId = requestAnimationFrame(doWrite);
      }
    };

    const onTopScroll = () => {
      const cur = top.scrollLeft;
      if (Math.abs(cur - lastLeftTop) <= 0.5) return; // vertical-only scroll
      lastLeftTop = cur;
      const immediate = performance.now() - lastWheelTop < 80;
      writeFollower(top, bottom, immediate);
    };

    const onBottomScroll = () => {
      const cur = bottom.scrollLeft;
      if (Math.abs(cur - lastLeftBottom) <= 0.5) return;
      lastLeftBottom = cur;
      const immediate = performance.now() - lastWheelBottom < 80;
      writeFollower(bottom, top, immediate);
    };

    top.addEventListener('scroll', onTopScroll, { passive: true });
    bottom.addEventListener('scroll', onBottomScroll, { passive: true });

    // Initial align by ratio
    writeFollower(top, bottom, true);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      top.removeEventListener('wheel', markWheelTop);
      bottom.removeEventListener('wheel', markWheelBottom);
      top.removeEventListener('scroll', onTopScroll);
      bottom.removeEventListener('scroll', onBottomScroll);
    };
  }, deps);
}
